import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import { RadioGroup, RadioGroupItem } from "design-system/atoms/RadioGroup"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { FormControl, FormField, FormItem, FormMessage } from "design-system/organisms/Form"
import { cn } from "design-system/utils"
import { Plus, Trash } from "lucide-react"
import { UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { GetWidgetConfigurationResponseData } from "schema"
import z from "zod"
import { WidgetConfigurationFormType } from ".."

type TypingLabelProps = {
  form: UseFormReturn<WidgetConfigurationFormType>
}

type TypingIndicatorStyleEnum = z.infer<
  typeof GetWidgetConfigurationResponseData
>["typingIndicatorStyle"]

export const TypingLabel = ({ form }: TypingLabelProps) => {
  const { t } = useTranslation()
  const languages = form.watch("languages")
  const formLabels = form.watch("typingLabel") ?? {}
  const typingIndicatorStyle = form.watch("typingIndicatorStyle")
  const isCycling = typingIndicatorStyle !== "dots"

  const styleOptions = [
    {
      label: t("campaigns.templates.widget.config.typingLabel.style.dots"),
      description: t("campaigns.templates.widget.config.typingLabel.style.dotsDescription"),
      value: "dots",
    },
    {
      label: t("campaigns.templates.widget.config.typingLabel.style.cycling"),
      description: t("campaigns.templates.widget.config.typingLabel.style.cyclingDescription"),
      value: "cycling",
    },
  ]

  // Derive flat row list directly from form value — no local state needed
  const rows = Object.entries(formLabels).flatMap(([locale, texts]) =>
    texts.map((text, i) => ({ locale, text, i, key: `${locale}-${i}` })),
  )

  const onAddNew = () => {
    const locale = languages.at(0)?.value ?? ""
    // No trigger — the empty string is temporarily invalid; validate on blur
    form.setValue("typingLabel", { ...formLabels, [locale]: [...(formLabels[locale] ?? []), ""] })
  }

  const onChangeText = (locale: string, i: number, text: string) => {
    const texts = [...(formLabels[locale] ?? [])]
    texts[i] = text
    form.setValue("typingLabel", { ...formLabels, [locale]: texts })
  }

  const onChangeLocale = (prevLocale: string, i: number, nextLocale: string) => {
    const next = { ...formLabels }
    const prevTexts = [...(next[prevLocale] ?? [])]
    const [text = ""] = prevTexts.splice(i, 1)
    if (prevTexts.length) next[prevLocale] = prevTexts
    else delete next[prevLocale]
    next[nextLocale] = [...(next[nextLocale] ?? []), text]
    form.setValue("typingLabel", Object.keys(next).length ? next : undefined)
    form.trigger()
  }

  const onDelete = (locale: string, i: number) => {
    const next = { ...formLabels }
    const texts = [...(next[locale] ?? [])]
    texts.splice(i, 1)
    if (texts.length) next[locale] = texts
    else delete next[locale]
    form.setValue("typingLabel", Object.keys(next).length ? next : undefined)
    form.trigger()
  }

  return (
    <div className="flex flex-col gap-4">
      <FormField
        control={form.control}
        name="typingIndicatorStyle"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <RadioGroup
                value={field.value}
                onValueChange={(value) =>
                  form.setValue(field.name, value as TypingIndicatorStyleEnum)
                }
                className="grid grid-cols-2 gap-3"
              >
                {styleOptions.map((option) => (
                  <label
                    key={option.value}
                    htmlFor={`typingIndicatorStyle-${option.value}`}
                    className={cn(
                      "flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors",
                      field.value === option.value
                        ? "border-primary bg-primary/5"
                        : "border-muted-foreground/20 hover:border-muted-foreground/40",
                    )}
                  >
                    <RadioGroupItem
                      id={`typingIndicatorStyle-${option.value}`}
                      value={option.value}
                    />
                    <div className="flex flex-col gap-0.5">
                      <span className="text-base font-medium leading-snug">{option.label}</span>
                      <span className="text-sm text-muted-foreground">{option.description}</span>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {isCycling && (
        <div className="w-full">
          <div className="grid gap-2 mb-4">
            <span className="text-sm text-muted-foreground">
              {t("campaigns.templates.widget.config.typingLabel.style.cyclingHint")}
            </span>
            {rows.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-2">
                <div className="col-span-1 lg:col-span-9">
                  <Label>{t("campaigns.templates.widget.config.typingLabel.label")}</Label>
                </div>
                <div className="col-span-1 lg:col-span-2">
                  <Label>{t("campaigns.templates.widget.config.typingLabel.language")}</Label>
                </div>
              </div>
            )}
            {rows.map(({ locale, text, i, key }) => (
              <div key={key} className="grid grid-cols-1 lg:grid-cols-12 gap-2 group/row">
                <div className="col-span-1 lg:col-span-9">
                  <Input
                    value={text}
                    onChange={(ev) => onChangeText(locale, i, ev.target.value)}
                    onBlur={() => form.trigger()}
                  />
                </div>
                <div className="col-span-1 lg:col-span-2">
                  <Select
                    onValueChange={(nextLocale) => onChangeLocale(locale, i, nextLocale)}
                    value={locale}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {languages.map((language) => (
                        <SelectItem key={language.value} value={language.value}>
                          {language.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-1 flex justify-end items-center duration-100 opacity-0 group-hover/row:opacity-100">
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    onClick={() => onDelete(locale, i)}
                  >
                    <Trash />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <Button variant="outline" onClick={onAddNew} type="button">
            <Plus />
            <span>{t("campaigns.templates.widget.config.typingLabel.addNew")}</span>
          </Button>
        </div>
      )}
    </div>
  )
}
